import React, { useMemo, useState } from "react";
import styled from "styled-components/macro";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExpandLess from "@material-ui/icons/ExpandLess";
import { JobCard } from "./job_card";
import { theme, getStatusMeta } from "./theme";

const JobGroupedView = ({
  jobs,
  printers,
  jobSelected,
  onJobClick,
  renderColor,
  selectedIds,
  onToggleCheck,
  onOpenPrinter,
}) => {
  const groups = useMemo(() => {
    const map = new Map();
    jobs.forEach((job) => {
      const key = job.roomName || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(job);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [jobs]);

  const [collapsed, setCollapsed] = useState({});

  if (!jobs.length) {
    return <Empty>No jobs match your filters.</Empty>;
  }

  return (
    <Wrapper>
      {groups.map(([roomName, groupJobs]) => {
        const statusCounts = {};
        groupJobs.forEach((j) => {
          statusCounts[j.status] = (statusCounts[j.status] || 0) + 1;
        });
        const isCollapsed = !!collapsed[roomName];

        return (
          <Group key={roomName}>
            <GroupHeader onClick={() => setCollapsed((prev) => ({ ...prev, [roomName]: !prev[roomName] }))}>
              {isCollapsed ? <ExpandMore style={{ fontSize: 18 }} /> : <ExpandLess style={{ fontSize: 18 }} />}
              <GroupTitle>{roomName}</GroupTitle>
              <GroupCount>{groupJobs.length}</GroupCount>
              <ChipsRow>
                {Object.entries(statusCounts).map(([status, count]) => {
                  const meta = getStatusMeta(status);
                  return (
                    <StatusChip key={status} chipColor={meta.color} chipBackground={meta.background}>
                      {meta.label} {count}
                    </StatusChip>
                  );
                })}
              </ChipsRow>
            </GroupHeader>
            {!isCollapsed ? (
              <GroupBody>
                {groupJobs.map((item) => (
                  <JobCard
                    key={item.id}
                    renderColor={renderColor}
                    printer={printers?.[item.printer_id]}
                    doc={item}
                    jobSelected={jobSelected}
                    onJobClick={onJobClick}
                    selectable
                    checked={selectedIds.has(item.id)}
                    onToggleCheck={onToggleCheck}
                    onOpenPrinter={onOpenPrinter}
                  />
                ))}
              </GroupBody>
            ) : null}
          </Group>
        );
      })}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Group = styled.div`
  border: 1px solid ${theme.border};
  border-radius: ${theme.radius};
  overflow: hidden;
  background: ${theme.surface};
`;

const GroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  outline: none;
  border: none;
  cursor: pointer;
  background: ${theme.background};
  padding: 12px 16px;
  font-family: ${theme.fontFamily};
  text-align: start;
  color: ${theme.textMuted};
`;

const GroupTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.text};
`;

const GroupCount = styled.div`
  font-size: 12px;
  color: ${theme.textFaint};
`;

const ChipsRow = styled.div`
  margin-left: auto;
  display: flex;
  gap: 6px;
`;

const StatusChip = styled.div`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: ${theme.radiusPill};
  color: ${({ chipColor }) => chipColor};
  background: ${({ chipBackground }) => chipBackground};
  white-space: nowrap;
`;

const GroupBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${theme.textMuted};
  font-size: 14px;
  font-family: ${theme.fontFamily};
`;

export { JobGroupedView };
